import * as Y from 'yjs';

import { Logger } from '@gitbook/util-debug';

import { markYMutation } from '../utils';
import type { YSlateBlock, YSlateDocument, YSlateFragments, YSlateInline } from './utils';

const logger = Logger('hive-core:normalizeYDocument');

/**
 * Normalize a Y document to prevent duplicated keys that will crash slate.
 * It returns the number of duplicated keys that it removed.
 */
export function normalizeYDocument(yDocument: YSlateDocument): number {
    const keys = new Set<string>();
    const duplicatedKeys = new Set<string>();

    normalizeYNode(yDocument, keys, duplicatedKeys);

    if (duplicatedKeys.size > 0) {
        logger.warning(
            `Found ${duplicatedKeys.size} duplicated keys in Y document. This is a bug in Hive.`
        );
    }

    return duplicatedKeys.size;
}

function normalizeYNode(
    yNode: YSlateInline | YSlateBlock,
    keys: Set<string>,
    duplicatedKeys: Set<string>
) {
    normalizeYNodes(yNode, keys, duplicatedKeys);
    const fragments = yNode.getAttribute('fragments');
    if (fragments) {
        normalizeYFragments(fragments, keys, duplicatedKeys);
    }
}

function normalizeYFragments(
    yFragments: YSlateFragments,
    keys: Set<string>,
    duplicatedKeys: Set<string>
) {
    normalizeYNodes(yFragments, keys, duplicatedKeys);
}

function normalizeYNodes(yNode: Y.XmlFragment, keys: Set<string>, duplicatedKeys: Set<string>) {
    let shift = 0;

    yNode.forEach((child, index) => {
        const key = child.getAttribute('key');

        if (keys.has(key)) {
            duplicatedKeys.add(key);

            // We manually mark the mutation here because the call can be wrapped in the same transaction
            // as yToDocument and the mutation will be missed by the memoization.
            markYMutation(yNode);

            yNode.delete(index - shift);
            shift++;
            return;
        }
        keys.add(key);

        if (child instanceof Y.XmlText) {
            return;
        }
        normalizeYNode(child, keys, duplicatedKeys);
    });
}
